import { useSiteContent } from '@/hooks/useSiteContent';
import { useTranslation } from 'react-i18next';
import ContentState from '@/components/feature/ContentState';

const countryFlags: Record<string, string> = {
  VN: '🇻🇳', TH: '🇹🇭', SG: '🇸🇬', HK: '🇭🇰', PH: '🇵🇭', ID: '🇮🇩',
  GB: '🇬🇧', DE: '🇩🇪', NL: '🇳🇱', DK: '🇩🇰', US: '🇺🇸', CA: '🇨🇦',
  AU: '🇦🇺', NZ: '🇳🇿', AE: '🇦🇪',
};

interface CompanyInfoData {
  title: string;
  highlight: string;
  company: { name: string; license: string; address: string; phone: string; email: string };
  offices: { city: string; address: string }[];
  payment: { title: string; methods: string[] };
  bank: { title: string; name: string; bank: string; account: string; swift: string };
  countries: { name: string; code: string }[];
}

export default function CompanyInfo() {
  const { data, loading, error, refresh } = useSiteContent<CompanyInfoData>('why_us_company_info');
  const { t } = useTranslation();

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="w-full bg-primary-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-4">
            {data.title}{' '}
            <span className="text-primary-700">{data.highlight}</span>
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
            {/* Left: Company Details */}
            <div className="space-y-6">
              {/* Company Card */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-building-line text-white" />
                  </div>
                  <h3 className="text-sm font-bold tracking-wider uppercase text-foreground-700">
                    {t('whyus.companyInfo')}
                  </h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-foreground-500 uppercase tracking-wider">{t('whyus.name')}</p>
                    <p className="text-sm font-semibold text-foreground-950">{data.company.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 uppercase tracking-wider">{t('whyus.license')}</p>
                    <p className="text-sm font-semibold text-foreground-950">{data.company.license}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 uppercase tracking-wider">{t('whyus.address')}</p>
                    <p className="text-sm text-foreground-700">{data.company.address}</p>
                  </div>
                  <div className="flex gap-6">
                    <div>
                      <p className="text-xs text-foreground-500 uppercase tracking-wider">{t('whyus.phone')}</p>
                      <p className="text-sm font-semibold text-foreground-950">{data.company.phone}</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 uppercase tracking-wider">{t('whyus.email')}</p>
                      <p className="text-sm font-semibold text-foreground-950">{data.company.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Offices */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h3 className="text-sm font-bold tracking-wider uppercase text-foreground-700 mb-4">
                  {t('whyus.ourOffices')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {data.offices.map((office) => (
                    <div key={office.city} className="flex items-start gap-2.5">
                      <i className="ri-map-pin-line text-primary-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-foreground-950">{office.city}</p>
                        <p className="text-xs text-foreground-600">{office.address}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h3 className="text-sm font-bold tracking-wider uppercase text-foreground-700 mb-4">
                  {data.payment.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {data.payment.methods.map((method) => (
                    <span
                      key={method}
                      className="bg-secondary-100 text-secondary-800 text-xs font-semibold px-3 py-1.5 rounded-full"
                    >
                      {method}
                    </span>
                  ))}
                </div>
              </div>

              {/* Bank */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h3 className="text-sm font-bold tracking-wider uppercase text-foreground-700 mb-4">
                  {data.bank.title}
                </h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-foreground-500">{t('whyus.accountName')}</span> <span className="font-semibold text-foreground-950">{data.bank.name}</span></p>
                  <p><span className="text-foreground-500">{t('whyus.bank')}</span> <span className="font-semibold text-foreground-950">{data.bank.bank}</span></p>
                  <p><span className="text-foreground-500">{t('whyus.account')}</span> <span className="font-semibold text-foreground-950">{data.bank.account}</span></p>
                  <p><span className="text-foreground-500">{t('whyus.swift')}</span> <span className="font-semibold text-foreground-950">{data.bank.swift}</span></p>
                </div>
              </div>
            </div>

            {/* Right: Map + Countries */}
            <div className="space-y-6">
              {/* Map placeholder */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h3 className="text-sm font-bold tracking-wider uppercase text-foreground-700 mb-4">
                  {t('whyus.ourCoverage')}
                </h3>
                <div className="relative aspect-[4/3] bg-primary-50 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7836392.494482044!2d104.7992575!3d15.92666505!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31157a4d736a1e5f%3A0xb03bb0c9e2fe62be!2sVietnam!5e0!3m2!1sen!2s!4v1700000000000!5m2!1sen!2s"
                    className="w-full h-full border-0"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={t('whyus.mapTitle')}
                  />
                </div>
              </div>

              {/* Countries */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                <h3 className="text-sm font-bold tracking-wider uppercase text-foreground-700 mb-4">
                  {t('whyus.travelersFrom')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {data.countries.map((country) => (
                    <div
                      key={country.code}
                      className="flex items-center gap-2 py-1.5"
                    >
                      <span className="text-base">{countryFlags[country.code] || '🌏'}</span>
                      <span className="text-xs text-foreground-700">{country.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}